<template>
  <div id="system-health-table-container">
    <div class="sht-header-container">
      <div class="sh-top-right-buttons">
        <SACTableSearch
          :search="shTableSearch"
          @input="changeSearch"
        />
        <button
          type="button"
          class="margin-left-16"
          @click="toggleActionModal(true, 'csv-confirmation')"
        >
          <img
            alt="export"
            src="@shared/assets/img/general/download.svg"
          >
        </button>
      </div>
    </div>
    <div class="sht-container">
      <CategoryTab
        :loading="loadingTableData"
        :leftActiveTab="activeProfileTab"
        :rightActiveTab="activeHealthTab"
        :leftCategory="[{
          value: 'complete',
          label: 'Active',
          count: completedProfileCount,
        }, {
          value: 'incomplete',
          label: 'Unvalidated',
          count: incompletedProfileCount,
        }]"
        :rightCategory="[{
          value: 'severe',
          label: 'Severe',
          count: severeSystemCount,
          className: 'red-info',
        }, {
          value: 'mild',
          label: 'Mild',
          count: mildSystemCount,
          className: 'yellow-info',
        }, {
          value: 'normal',
          label: 'Normal',
          count: normalSystemCount,
          className: 'green-info',
        }]"
        :leftToggle="toggleProfileTab"
        :rightToggle="toggleHealthTab"
      />
      <v-data-table
        :headers="systemHealthTableHeaders"
        :items="tableData"
        :options="shTableOptions"
        :loading="loadingTableData"
        :search="shTableSearch"
        :single-expand="true"
        item-key="key"
        hide-default-footer
        fixed-header
        show-expand
        :custom-sort="customSort"
        @item-expanded="loadDetails"
      >
        <template #[`item.customer.name`]="{ item }">
          <td>
            <router-link :to="`/member/${item.customer.id}`">
              {{ item.customer.name }}
            </router-link>
          </td>
        </template>
        <template #[`item.systemHealthRating`]="{ item }">
          <td
            v-if="item.systemHealthRating"
            class="shr-container"
          >
            <span class="shr-button-container">
              <span
                :class="[
                  item.systemHealthRating === 'Normal' ? 'normal-hr-dot' : '',
                  item.systemHealthRating === 'Mild' ? 'mild-hr-dot' : '',
                  item.systemHealthRating === 'Severe' ? 'severe-hr-dot' : '',
                ]"
              />
              <span>
                {{ item.systemHealthRating }}
              </span>
            </span>
          </td>
        </template>
        <template #[`header.notificationAlert`]="{ }">
          <SACTableInlineFilter
            :filters="alertsCategory"
            :change="changeInlineFilter"
            :profileTab="activeProfileTab"
            :loading="loadingTableData"
          />
        </template>
        <template #[`item.notificationAlert`]="{ item }">
          <td>
            <div class="sht-alerts">
              <div
                v-if="checkAlerts(item, 'leak')"
                class="sht-alert-container"
              >
                <span class="sht-a-tooltip">Leak</span>
                <img
                  alt="water-drop"
                  class="alerts-icon"
                  src="@shared/assets/img/alerts/water-drop-icon.png"
                >
              </div>
              <div
                v-if="checkAlerts(item, 'performance')"
                class="sht-alert-container"
              >
                <span class="sht-a-tooltip">Performance</span>
                <img
                  alt="perf-gauge"
                  class="alerts-icon"
                  src="@shared/assets/img/alerts/performance-gauge-icon.png"
                >
              </div>
              <div
                v-if="checkAlerts(item, 'filter')"
                class="sht-alert-container"
              >
                <span class="sht-a-tooltip">Filter</span>
                <img
                  alt="air-filter"
                  class="alerts-icon"
                  src="@shared/assets/img/alerts/air-filter-icon.png"
                >
              </div>
              <div
                v-if="checkAlerts(item, 'battery')"
                class="sht-alert-container"
              >
                <span class="sht-a-tooltip">Battery</span>
                <img
                  alt="baterry"
                  class="alerts-icon"
                  src="@shared/assets/img/alerts/battery-half-full-icon.png"
                >
              </div>
              <div
                v-if="checkAlerts(item, 'device')"
                class="sht-alert-container"
              >
                <span class="sht-a-tooltip">Device</span>
                <img
                  alt="device"
                  class="alerts-icon"
                  src="@shared/assets/img/alerts/device-icon.png"
                >
              </div>
            </div>
          </td>
        </template>
        <template #[`item.deviceAC.systemAge`]="{ item }">
          <td>
            {{ getSystemAge(item.deviceAC.systemAge) }}
          </td>
        </template>
        <template #[`item.firstAlert`]="{ item }">
          <td>
            {{ customDateFilter(item.firstAlert, dateFormat) }}
          </td>
        </template>
        <template #[`item.deviceAC.cquestionnaire.annualInspection`]="{ item }">
          <td>
            {{ customDateFilter(item.deviceAC.cquestionnaire.annualInspection, dateFormat) }}
          </td>
        </template>
        <template #[`item.recentAlert`]="{ item }">
          <td>
            {{ customDateFilterMoment(item.recentAlert, dateFormat) }}
          </td>
        </template>
        <template #[`item.deviceAC.lastServiceDate`]="{ item }">
          <td>
            {{ customDateFilterMoment(item.deviceAC.lastServiceDate, dateFormat) }}
          </td>
        </template>
        <template #[`item.occurrences`]="{ item }">
          <td>
            {{ getOccurrence(item.occurrences, activeAlertTab) }}
          </td>
        </template>
        <template #[`expanded-item`]="{ }">
          <td
            class="expand-container"
            colspan="11"
          >
            <div
              v-if="expandableLoading"
              class="loading-container"
            >
              <img
                alt="loading"
                src="@shared/assets/img/general/loading.svg"
              >
            </div>
            <div v-else>
              <div
                v-if="isSmacAdmin && chronicAlerts.length > 0"
                class="expand-top-container"
              >
                <ChronicDetail />
              </div>
              <div class="expand-top-container">
                <HealthInsightDetail />
              </div>
              <div class="expand-bottom-container">
                <HvacDetail />
                <FilterDetail />
              </div>
            </div>
          </td>
        </template>
      </v-data-table>
    </div>
    <SACTableFooter
      :totalRecords="tableData.length"
      :rowCount="shTableOptions.itemsPerPage"
      :changeRowCount="changeRowCount"
      :page="shTableOptions.page"
      :totalPage="systemHealthTableRecordCount"
      :changePage="changePage"
      :loading="loadingTableData"
    />
  </div>
</template>

<script>
import systemHealthMixin from '@shared/mixins/systemHealthMixin';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';

import SACTableSearch from '@shared/components/table/SACTableSearch.vue';
import SACTableInlineFilter from '@shared/components/table/SACTableInlineFilter.vue';
import SACTableFooter from '@shared/components/table/SACTableFooter.vue';

import ChronicDetail from './ChronicDetail.vue';
import FilterDetail from './FilterDetail.vue';
import HvacDetail from './HvacDetail.vue';
import HealthInsightDetail from './HealthInsightDetail.vue';
import CategoryTab from '../shared/CategoryTab.vue';

export default {
  name: 'SystemHealthTable',
  components: {
    CategoryTab,
    SACTableSearch,
    SACTableInlineFilter,
    SACTableFooter,
    HealthInsightDetail,
    ChronicDetail,
    HvacDetail,
    FilterDetail,
  },
  mixins: [systemHealthMixin, unitPreferencesMixin],
  data() {
    return {
      show: false,
    };
  },
  destroyed() {
    this.$store.dispatch('resetExpandableRow');
  },
  methods: {
    customSort(items, index, isDesc) {
      this.$store.dispatch('changeSHTableOption', {
        field: 'sortBy',
        value: index,
      });

      this.$store.dispatch('changeSHTableOption', {
        field: 'sortDesc',
        value: isDesc,
      });

      items.sort((a, b) => {
        if (index[0] === 'customer.name') {
          if (!a.customer.name) {
            return 1;
          }
          if (!b.customer.name) {
            return -1;
          }
          if (!isDesc[0]) {
            return a.customer.name.toLowerCase() < b.customer.name.toLowerCase() ? -1 : 1;
          }
          return b.customer.name.toLowerCase() < a.customer.name.toLowerCase() ? -1 : 1;
        }
        if (index[0] === 'deviceAC.systemName') {
          if (!a.deviceAC.systemName) {
            return 1;
          }
          if (!b.deviceAC.systemName) {
            return -1;
          }
          const systemNameA = a.deviceAC.systemName.toLowerCase();
          const systemNameB = b.deviceAC.systemName.toLowerCase();
          if (!isDesc[0]) {
            return systemNameA < systemNameB ? -1 : 1;
          }
          return systemNameB < systemNameA ? -1 : 1;
        }
        if (index[0] === 'deviceAC.systemAge') {
          if (!a.deviceAC.systemAge) {
            return 1;
          }
          if (!b.deviceAC.systemAge) {
            return -1;
          }
          if (!isDesc[0]) {
            return a.deviceAC.systemAge < b.deviceAC.systemAge ? -1 : 1;
          }
          return b.deviceAC.systemAge < a.deviceAC.systemAge ? -1 : 1;
        }
        if (index[0] === 'systemHealthRating') {
          if (!a.systemHealthRating) {
            return 1;
          }
          if (!b.systemHealthRating) {
            return -1;
          }
          if (!isDesc[0]) {
            return a.systemHealthRating < b.systemHealthRating ? -1 : 1;
          }
          return b.systemHealthRating < a.systemHealthRating ? -1 : 1;
        }
        if (index[0] === 'deviceAC.uId') {
          if (!a.deviceAC.uId) {
            return 1;
          }
          if (!b.deviceAC.uId) {
            return -1;
          }
          if (!isDesc[0]) {
            return a.deviceAC.uId < b.deviceAC.uId ? -1 : 1;
          }
          return b.deviceAC.uId < a.deviceAC.uId ? -1 : 1;
        }
        if (index[0] === 'firstAlert') {
          if (!a.firstAlert) {
            return 1;
          }
          if (!b.firstAlert) {
            return -1;
          }
          const dateA = new Date(a.firstAlert);
          const dateB = new Date(b.firstAlert);

          if (!isDesc[0]) {
            return dateA < dateB ? -1 : 1;
          }
          return dateB < dateA ? -1 : 1;
        }
        if (index[0] === 'recentAlert') {
          if (!a.recentAlert) {
            return 1;
          }
          if (!b.recentAlert) {
            return -1;
          }
          const dateA = new Date(a.recentAlert);
          const dateB = new Date(b.recentAlert);

          if (!isDesc[0]) {
            return dateA < dateB ? -1 : 1;
          }
          return dateB < dateA ? -1 : 1;
        }
        return 0;
      });

      return items;
    },
    loadDetails({ item }) {
      const {
        id,
        deviceAC,
      } = item;
      const { uid } = deviceAC;
      this.$store.dispatch('setExpandableRow', {
        id,
        uid,
      });
    },
    changePage(page) {
      this.$store.dispatch('changeSHTableOption', {
        field: 'page',
        value: page,
      });
    },
    changeRowCount(rowCount) {
      this.$store.dispatch('changeSHTableOption', {
        field: 'itemsPerPage',
        value: rowCount,
      });
    },
    changeSort([item]) {
      this.$store.dispatch('changeSHTableOption', {
        field: 'sortBy',
        value: [item],
      });
    },
    changeSortDesc([item]) {
      this.$store.dispatch('changeSHTableOption', {
        field: 'sortDesc',
        value: [item],
      });
    },
    changeSearch(value) {
      this.$store.dispatch('changeHomeValue', {
        field: 'shTableSearch',
        value,
      });
    },
    toggleHealthTab(hTab) {
      if (!this.loadingTableData) {
        const healthTab = hTab !== this.activeHealthTab ? hTab : '';
        this.$store.dispatch('changeSHTableAlertType', {
          healthTab,
          profileTab: this.activeProfileTab,
        });
      }
    },
    toggleProfileTab(tab) {
      if (!this.loadingTableData) {
        this.$store.dispatch('changeSHTableAlertType', {
          profileTab: tab,
          healthTab: this.activeHealthTab,
        });
      }
    },
    changeInlineFilter(filter, value) {
      this.$store.dispatch('changeSHTableAlertType', {
        profileTab: this.activeProfileTab,
        healthTab: this.activeHealthTab,
        type: 'category',
        filter,
        value,
      });
    },
  },
};
</script>

<style lang="scss">

#system-health-table-container {
  max-width: 1705px;
  width: 100%;

  .sh-select-container {
    margin-bottom: 12px;
  }

  .sh-top-right-buttons {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .sht-header-container {
    display: flex;
    align-items: center;
    margin-bottom: 18px;
    justify-content: flex-end;

    .sht-header {
      font-size: 1.5625rem;
      color: $N100;
      font-weight: 700;
    }
  }

  .sht-container {
    background-color: $WHITE;
    box-shadow: 0 0 7px 2px rgba(9, 15, 32, 0.02);
    border-radius: 6px;
    padding-bottom: 12px;

    .v-data-table__wrapper {
      height: 700px;

      thead {
        tr {
          th {
            background-color: $N100;
            color: $WHITE;
            font-size: 15px;
            font-weight: 500;

            .mdi-checkbox-blank-outline::before,
            .mdi-checkbox-marked::before,
            .mdi-minus-box::before,
            .mdi-arrow-up::before {
              color: $WHITE;
            }

            &.sortable {
              i {
                display: none;
              }
            }

            &.active {
              i {
                display: inline-block;
              }
            }
          }
        }
      }

      tbody {
        tr {
          .expand-container {
            padding: 1rem;
            border: 1px solid $LIGHTGRAY;
            background-color: $NBLUE10;

            .loading-container {
              flex: 1;
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .expand-top-container {
              margin-bottom: 16px;
              display: flex;

              .et-prev-button {
                position: relative;
                display: flex;
                flex-direction: column;
                width: 50px;
                align-items: center;
                background-color: $N20;

                .et-left-img {
                  width: 20px;
                  height: 20px;
                  margin-right: 5px;
                  margin-top: 16px;
                }

                .et-prev-text {
                  position: absolute;
                  transform-origin: 0 0;
                  transform: rotate(-90deg);
                  top: 70px;
                }
              }
            }

            .expand-bottom-container {
              display: grid;
              grid-auto-flow: column;
              gap: 1rem;
            }
          }

          td {
            border-bottom: 0;

            .mdi-checkbox-marked::before {
              color: $NBLUE100;
            }
          }

          &:hover {
            background: none;
          }

          &:nth-child(even) {
            background-color: $NBLUE5;
          }
        }
      }
    }

    .shr-container {
      .shr-button-container {
        text-align: center;
        width: 80px;
        gap: 5px;
        background-color: $N10;
        border-radius: 20px;
        padding: 4px 8px;
        font-weight: 600;
        font-size: 13px;
      }

      .normal-hr-dot {
        background-color: $BASIL100;
        height: 12px;
        width: 12px;
        border-radius: 50%;
        display: inline-block;
      }

      .mild-hr-dot {
        background-color: $SUNSET100;
        height: 12px;
        width: 12px;
        border-radius: 50%;
        display: inline-block;
      }

      .severe-hr-dot {
        background-color: $TOMATO100;
        height: 12px;
        width: 12px;
        border-radius: 50%;
        display: inline-block;
      }
    }

    .sht-alerts {
      display: flex;

      .sht-alert-container {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;

        .sht-a-tooltip {
          display: none;
          position: absolute;
          bottom: 25px;
          background-color: $WHITE;
          padding: 4px 8px 4px 8px;
          border-radius: 6px;
          box-shadow: 0px 3px 6px rgba(9, 15, 32, 0.07);
          z-index: 10;
          white-space: nowrap;
          font-size: 0.75rem;
          color: $BLACK;

          &:after {
            transform: rotate(180deg);
            content: '';
            position: absolute;
            bottom: -5px;
            left: 50%;
            width: 0;
            height: 0;
            border: 8px solid transparent;
            border-bottom-color: $WHITE;
            border-top: 0;
            margin-left: -8px;
            margin-top: -8px;
          }
        }

        .alerts-icon {
          height: 20px;
          margin-left: 2px;
        }

        &:not(:last-child) {
          margin-right: 2px;
        }

        &:hover {
          .sht-a-tooltip {
            display: block;
          }
        }
      }
    }
  }
}
</style>
